import React, { ReactElement, useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { observer } from 'mobx-react-lite';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useMst } from '../../models/stores/Root';
import UtilityService from '../../services/UtilityService';
import GuestCheckoutPage from '../GuestCheckoutPage';
import ThankYouPage from '../ThankYouPage';
import ErrorPage from '../ErrorPage';
import OneClickPage from '../OneClickPage';
import LoadingView from '../views/LoadingView';
import AmazonPayPage from '../AmazonPayPage';
import PayPalPage from '../PayPalPage';
import { IPayPalExpress } from '../../models/stores/sub-stores/Configuration/PayPalExpress';
import WindowService from '../../services/WindowService';
import EmptyCartPage from '../EmptyCartPage';
import {
  applyCheckoutPanelFormStyles,
  applyCheckoutPanelMainStyles,
  applyGlobalStyles,
  applyLegacyStyles,
  applySummaryPanelCartStyles,
  applySummaryPanelOrderStyles,
} from '../../utils/customStyles';
import WrappedStripeWalletLoader from '../../components/payment/WrappedStripeWalletLoader';
import WalletLoadingMessage from '../../components/content/WalletLoadingMessage';

const storeUrl = UtilityService.getBaseUrl(window.location.origin);

type LoadingMessageState = {
  isLoading: boolean;
  loadingMessage?: string | React.ReactNode;
};

const MasterPage: React.FC = observer(() => {
  const rootStore = useMst();
  const { configuration, cart, customer } = rootStore;
  const { colors, styles } = configuration;

  const isGTMAvailable =
    (configuration.features.enableSpreedly &&
      configuration.payments.enabledPaymentMethods.some(
        x => x.methodType.toLowerCase() === 'credit card'
      )) ||
    !configuration.payments.enabledPaymentMethods.some(
      x => x.methodType.toLowerCase() === 'credit card'
    );

  if (
    configuration.features.enableGTM &&
    configuration.features.gtmID &&
    isGTMAvailable
  ) {
    const tagManagerArgs = {
      gtmId: configuration.features.gtmID,
    };
    TagManager.initialize(tagManagerArgs);
  }

  const cartId = UtilityService.getCartId();
  const [loadingState, setLoadingState] = useState<LoadingMessageState>({
    isLoading: false,
  });

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      '\x1b[32m%s\x1b[0m',
      'Release Version:',
      process.env.REACT_APP_PACKAGE_VERSION
    );
    rootStore.applyInitialStyleFromQs();
  }, [rootStore]);

  if (configuration.isEnabled('Feature_Checkout_Customization_Enabled')) {
    applyGlobalStyles(styles);
    applyCheckoutPanelMainStyles(styles);
    applyCheckoutPanelFormStyles(styles);
    applySummaryPanelCartStyles(styles);
    applySummaryPanelOrderStyles(styles);
  } else {
    applyLegacyStyles(colors);
  }

  useEffect(() => {
    if (customer.isLoading) {
      setLoadingState({
        isLoading: true,
        loadingMessage: 'Loading your preferences',
      });
    } else if (cart.fetchingShippingMethods) {
      if (!rootStore.stateMatches('Guest')) {
        setLoadingState({
          isLoading: true,
          loadingMessage: 'Updating Your Cart',
        });
      }
    } else if (rootStore.currentStep === 'Loading') {
      setLoadingState({
        isLoading: true,
        loadingMessage: 'Loading Secure Checkout',
      });
    } else if (rootStore.stateMatches('LoadWallet')) {
      const snapShot = rootStore.machine?.getSnapshot();
      setLoadingState({
        isLoading: true,
        loadingMessage: (
          <WalletLoadingMessage type={snapShot?.context.stripeWallet?.token} />
        ),
      });
    } else if (rootStore.stateMatches('AmazonPay.Checkout.Loading')) {
      setLoadingState({
        isLoading: true,
        loadingMessage: 'Loading Amazon Pay',
      });
    } else if (rootStore.stateMatches('PayPal.Checkout.Loading')) {
      setLoadingState({
        isLoading: true,
        loadingMessage: 'Loading PayPal Data',
      });
    } else {
      setLoadingState({
        isLoading: false,
        loadingMessage: '',
      });
    }
  }, [
    customer.isLoading,
    cart.fetchingShippingMethods,
    rootStore.currentStep,
    rootStore,
    cart.isLoading,
  ]);

  useEffect(() => {
    if (
      configuration.isConfigured &&
      cartId &&
      rootStore.stateMatches('Loading')
    ) {
      cart.setCartId(cartId);
      rootStore.initializeCheckout();
    }
  }, [
    configuration.isConfigured,
    configuration.features.disableCustomerLogin,
    cartId,
    cart,
    customer,
    rootStore,
  ]);

  const wrapPayPalScriptProvider = (
    children: React.ReactNode
  ): ReactElement => {
    const paypalConfig: IPayPalExpress = configuration.paypalExpress;
    return configuration.canDisplayPayPalButtons ? (
      <PayPalScriptProvider
        options={{
          'client-id': paypalConfig.publicPartnerId as string,
          'merchant-id': paypalConfig.publicMerchantId as string,
          currency: paypalConfig.defaultCurrency as string,
          intent: paypalConfig.isDelayedCapture ? 'authorize' : 'capture',
          commit: false,
          'disable-funding': paypalConfig.fundingToDisable,
          'enable-funding': configuration.features.enablePayPalPpcp
            ? paypalConfig.fundingToEnable
            : undefined,
          'data-client-token': paypalConfig.acpClientToken ?? undefined,
          components: 'buttons,messages,hosted-fields',
          'data-partner-attribution-id': 'Volusion_PCP_V1',
          'data-csp-nonce': window.CSP_NONCE,
        }}
      >
        {children}
      </PayPalScriptProvider>
    ) : (
      <div>{children}</div>
    );
  };

  const renderPage = (): ReactElement | null => {
    const blankPage = (
      <div
        className="checkout-panel-main"
        style={{ minHeight: '100vh', minWidth: '100%' }}
      />
    );
    const isCartEmpty = !cartId || (cart.isPopulated && !cart.hasItems);
    if (isCartEmpty) {
      if (rootStore.currentStep !== 'ThankYou') {
        if (rootStore.cart.showEmptyCartPage) {
          return <EmptyCartPage />;
        }
        // Return user to homepage
        WindowService.navigate(storeUrl);
        return blankPage;
      }
      return <ThankYouPage />;
    }
    if (
      rootStore.stateMatches('Start') ||
      rootStore.stateMatches('CheckAuth') ||
      rootStore.stateMatches('Loading')
    ) {
      return blankPage;
    }
    if (rootStore.stateMatches('LoadWallet')) {
      return <WrappedStripeWalletLoader />;
    }
    if (rootStore.stateMatches('Guest')) {
      return <GuestCheckoutPage />;
    }

    if (rootStore.stateMatches('OneClick')) {
      return <OneClickPage />;
    }

    if (rootStore.stateMatches('PayPal')) {
      return <PayPalPage />;
    }
    if (rootStore.stateMatches('AmazonPay')) {
      return <AmazonPayPage />;
    }
    return <ErrorPage />;
  };

  return (
    <div className={configuration.isConfigured ? '' : 'empty-page-placeholder'}>
      {configuration.isConfigured && wrapPayPalScriptProvider(renderPage())}
      <LoadingView
        isOpen={loadingState.isLoading || cart.isLoading}
        message={loadingState.loadingMessage || cart.loadingMessage}
      />
    </div>
  );
});

export default MasterPage;
